// Page Salon Mobile - Design unifié
// Header simple "Salon" + Onglets Tous/Famille/Amis + Loupe recherche fullscreen

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Clock, Users, Plus, MessageSquare, Image, ChevronRight,
  Loader2, RefreshCw, SlidersHorizontal, Home, Heart, Briefcase
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de groupes
const GROUP_TYPES = [
  { id: 'famille', name: 'Famille', emoji: '👨‍👩‍👧‍👦', color: 'from-rose-500 to-pink-500' },
  { id: 'amis', name: 'Amis', emoji: '🎉', color: 'from-amber-500 to-orange-500' },
  { id: 'travail', name: 'Travail', emoji: '💼', color: 'from-blue-500 to-indigo-500' },
  { id: 'voisins', name: 'Voisins', emoji: '🏠', color: 'from-green-500 to-emerald-500' },
];

export default function MobileSalonPage({ isDesktopEmbed = false }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtres
  const [activeTab, setActiveTab] = useState('tous');
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    fetchGroups();
  }, []);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem('salon_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  const fetchGroups = async () => {
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/groups/my-groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchGroups();
    setRefreshing(false);
  };
  
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try { localStorage.setItem('salon_recent_searches', JSON.stringify(updated)); } catch {}
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('salon_recent_searches'); } catch {}
  };
  
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };

  const handleCreate = () => {
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    if (!token) {
      toast.info('Connecte-toi pour créer un salon');
      return;
    }
    navigate('/cockpit-particulier/groupes');
  };

  const getTypeColor = (type) => {
    const found = GROUP_TYPES.find(t => t.id === type);
    return found?.color || 'from-slate-500 to-gray-500';
  };
  
  // Filtrer les groupes
  const filteredGroups = groups.filter(g => {
    if (activeTab !== 'tous' && g.type !== activeTab) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return g.name?.toLowerCase().includes(query);
    }
    return true;
  });
  
  return (
    <div className={isDesktopEmbed ? "bg-white" : "min-h-screen bg-gray-50"} style={{ paddingBottom: isDesktopEmbed ? '0' : '80px' }}>
      {/* Barre Bêta + Header - Masqués en mode Desktop embed */}
      {!isDesktopEmbed && (
        <>
          {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}
          
          <header className="bg-white border-b border-gray-200 sticky top-[30px] z-50">
            <div className="flex items-center justify-between h-14 px-3">
              <div className="flex items-center gap-0.5">
                <h1 className="text-lg font-bold text-[#1E3A5F]">Salon</h1>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleRefresh} disabled={refreshing} className="p-2 hover:bg-gray-100 rounded-full">
                  <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="salon-search-btn">
                  <Search size={20} className="text-gray-600" />
                </button>
              </div>
            </div>
            
            <div className="flex border-b border-gray-100">
              <button 
                onClick={() => setActiveTab('tous')}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === 'tous' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}
              >
                <Users size={14} /> Tous
              </button>
              <button 
                onClick={() => setActiveTab('famille')}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === 'famille' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}
              >
                <Home size={14} /> Famille
              </button>
              <button 
                onClick={() => setActiveTab('amis')}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === 'amis' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}
              >
                <Heart size={14} /> Amis
              </button>
            </div>
          </header>
        </>
      )}
      
      {/* Contenu */}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-800">Mes Salons</h2>
            <p className="text-xs text-slate-500">{filteredGroups.length} salon{filteredGroups.length > 1 ? 's' : ''}</p>
          </div>
          <Button onClick={handleCreate} className="bg-gradient-to-r from-orange-500 to-amber-500 text-xs px-3 py-2">
            <Plus size={14} className="mr-1" /> Nouveau
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : filteredGroups.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 text-center border border-orange-200">
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-2">Crée ton premier salon !</h3>
            <p className="text-sm text-slate-600 mb-4">Invite ta famille ou tes amis à rejoindre un espace privé.</p>
            <Button onClick={handleCreate} className="bg-gradient-to-r from-orange-500 to-amber-500 text-sm px-4 py-2">
              <Plus size={14} className="mr-1.5" /> Créer mon premier salon
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {filteredGroups.map((group) => (
              <motion.div key={group.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-slate-200 overflow-hidden" onClick={() => navigate(`/cockpit-particulier/groupes/${group.id}`)}>
                <div className={`h-16 bg-gradient-to-r ${getTypeColor(group.type)} relative`}>
                  <div className="absolute bottom-2 left-3 flex items-center gap-2">
                    <span className="text-2xl">{group.emoji}</span>
                    <div>
                      <h3 className="text-base font-bold text-white">{group.name}</h3>
                      <p className="text-white/80 text-xs">{group.member_count} membre{group.member_count > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><MessageSquare size={12} /> {group.post_count || 0}</span>
                    <span className="flex items-center gap-1"><Image size={12} /> {group.photo_count || 0}</span>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#ffffff', zIndex: 99999 }}>
            <div className="flex items-center justify-end" style={{ padding: '8px 12px', paddingTop: 'max(8px, env(safe-area-inset-top, 8px))' }}>
              <button onClick={() => setShowFullscreenSearch(false)} style={{ width: '32px', height: '32px' }}>
                <X size={24} className="text-[#64748B]" />
              </button>
            </div>
            
            <div className="px-3 pb-3">
              <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(searchQuery); }} className="flex items-center w-full" style={{ height: '56px', border: '2px solid #E8650A', borderRadius: '28px', background: '#F8F9FA', overflow: 'hidden' }}>
                <motion.button type="submit" initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }} className="flex items-center justify-center flex-shrink-0" style={{ width: '56px', height: '56px', background: '#E8650A', borderRadius: '26px 0 0 26px', position: 'relative', left: '-2px' }}>
                  <Search size={24} className="text-white" />
                </motion.button>
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Nom du salon..." className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none" style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }} autoFocus />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="flex items-center justify-center mr-4" style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}>
                    <X size={14} className="text-white" />
                  </button>
                )}
              </form>
            </div>
            
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">Recherche dans : Salon</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold">Recherches récentes</p>
                  {recentSearches.length > 0 && <button onClick={clearRecentSearches} className="text-[11px] text-[#E8650A] font-medium">Effacer</button>}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button key={idx} onClick={() => handleSearchSubmit(search)} className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg">
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
